import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { isForbidden } from '@/analytics/reportKit'
import { listPath } from '@/card/recordHelpers'
import { StateView } from '@/card/record/StateView'
import { Icon, type IconName } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import type { Locale } from '@/i18n'
import type { Notification } from './api'
import { useNotifications } from './useNotifications'
import { NotificationSettings } from './NotificationSettings'
import '@/styles/work-analytics.css'

/**
 * Центр уведомлений (Notifications.dc.html; ТЗ §5.1, тикет #79 поверх #49).
 *
 * ## Лента — запросом, новое — живым каналом
 *
 * Всё накопленное приходит обычным запросом, а новое — через SSE (`useNotifications`).
 * Обрыв канала значит «новое появится с задержкой», а не «уведомлений нет», поэтому экран
 * при разорванном канале остаётся рабочим и называет состояние словами.
 *
 * ## Чего нет из макета
 *
 * Telegram и тихих часов: из каналов есть только почта (#118, «Настройки доставки» —
 * `NotificationSettings`). Рисовать переключатели каналов, которых нет, значило бы обещать
 * доставку, которой нет. Ответа на упоминание прямо из ленты тоже нет: у уведомления нет
 * идентификатора комментария. Ответить можно в записи — ссылка «Открыть» ведёт туда.
 */

type Show = 'all' | 'unread' | 'mention'

const KIND_ICON: Record<string, IconName> = {
  info: 'alert',
  warning: 'alert',
  task: 'work',
  approval: 'check',
  mention: 'comment',
  order: 'box',
}
const KIND_TONE: Record<string, string> = { mention: 'stage', approval: 'call', order: 'mail' }

export function NotificationsPage() {
  const { t, locale } = useApp()
  const { notifications, unread, connected, loading, error, markRead, markAllRead, reload } = useNotifications()
  const [show, setShow] = useState<Show>('all')
  const [kind, setKind] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)

  if (error && isForbidden(error)) {
    return (
      <div className="stack" style={{ gap: 14 }}>
        <div className="page-head">
          <h1 className="h1">{t('notifications.title')}</h1>
        </div>
        <StateView kind="forbidden" boxed title={t('notifications.forbidden.title')} description={t('notifications.forbidden.text')} />
      </div>
    )
  }

  const kinds = Array.from(new Set(notifications.map((n) => n.kind)))
  const visible = notifications
    .filter((n) => show !== 'unread' || !n.read)
    .filter((n) => show !== 'mention' || n.kind === 'mention')
    .filter((n) => !kind || n.kind === kind)
  const groups = groupByDay(visible, new Date(), t, locale)

  const readAll = () => {
    markAllRead().then(
      () => setNotice(null),
      (failure: unknown) => setNotice(failure instanceof ApiError ? failure.message : t('app.error')),
    )
  }

  return (
    <div className="stack" style={{ gap: 14 }}>
      <div className="page-head">
        <h1 className="h1">{t('notifications.title')}</h1>
        {unread > 0 && <span className="count-pill is-danger">{unread}</span>}
        <span className="spacer" />
        {/* Состояние канала названо словами: «живая доставка» и «обновится при открытии» —
            разные обещания, и молчащий экран второе выдаёт за первое. */}
        <span className={connected ? 'flag flag-success' : 'flag flag-warning'} role="status">
          <Icon name={connected ? 'check' : 'clock'} />
          {connected ? t('notifications.live') : t('notifications.offline')}
        </span>
        <button className="btn btn-secondary btn-sm" type="button" aria-expanded={settingsOpen}
                onClick={() => setSettingsOpen((open) => !open)}>
          <Icon name="mail" size="sm" />
          {t('notifications.settings.open')}
        </button>
      </div>
      <p className="page-lead">{t('notifications.lead')}</p>

      {settingsOpen && <NotificationSettings />}

      {notice && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span className="fg">{notice}</span>
        </div>
      )}

      {error ? (
        <StateView
          kind="error"
          boxed
          title={t('notifications.error.title')}
          description={error instanceof ApiError ? error.message : t('notifications.error.text')}
          actions={
            <button className="btn btn-primary btn-sm" type="button" onClick={reload}>
              <Icon name="refresh" size="sm" />
              {t('state.retry')}
            </button>
          }
          code={error instanceof ApiError ? String(error.status) : undefined}
        />
      ) : (
        <>
          <div className="toolbar">
            <div className="seg" role="group" aria-label={t('notifications.show')}>
              <button type="button" aria-pressed={show === 'all'} onClick={() => setShow('all')}>
                {t('notifications.seg.all')} <span className="n">{notifications.length}</span>
              </button>
              <button type="button" aria-pressed={show === 'unread'} onClick={() => setShow('unread')}>
                {t('notifications.seg.unread')} <span className="n">{unread}</span>
              </button>
              <button type="button" aria-pressed={show === 'mention'} onClick={() => setShow('mention')}>
                {t('notifications.seg.mention')} <span className="n">{notifications.filter((n) => n.kind === 'mention').length}</span>
              </button>
            </div>
            {kinds.length > 1 && <div className="vsep" />}
            {kinds.length > 1 &&
              kinds.map((code) => (
                <button key={code} className="chip" type="button" aria-pressed={kind === code} onClick={() => setKind(kind === code ? null : code)}>
                  {t(`notifications.kind.${code}`)} <span className="count">{notifications.filter((n) => n.kind === code).length}</span>
                </button>
              ))}
            <span className="spacer" />
            {unread > 0 && (
              <button className="btn btn-secondary btn-sm" type="button" onClick={readAll}>
                <Icon name="check" size="sm" />
                {t('notifications.markAllRead', { count: unread })}
              </button>
            )}
          </div>

          {loading ? (
            <div className="panel" aria-busy="true" aria-label={t('notifications.loading')}>
              {Array.from({ length: 8 }, (_, index) => (
                <div key={index} className="list-item" style={{ alignItems: 'flex-start' }}>
                  <span className="skel skel-c" style={{ width: 28, height: 28 }} />
                  <div className="stack" style={{ gap: 6, flex: 1, paddingTop: 4 }}>
                    <span className="skel" style={{ width: `${52 + ((index * 11) % 30)}%` }} />
                    <span className="skel" style={{ width: '40%', height: 8 }} />
                  </div>
                  <span className="skel" style={{ width: 40 }} />
                </div>
              ))}
            </div>
          ) : visible.length === 0 ? (
            <StateView
              kind="empty"
              boxed
              title={t(notifications.length === 0 ? 'notifications.empty' : 'notifications.emptyFiltered')}
              description={t(notifications.length === 0 ? 'notifications.emptyText' : 'notifications.emptyFilteredText', { count: notifications.length })}
              actions={
                notifications.length > 0 ? (
                  <button className="btn btn-secondary btn-sm" type="button" onClick={() => { setShow('all'); setKind(null) }}>
                    {t('notifications.showAll', { count: notifications.length })}
                  </button>
                ) : undefined
              }
            />
          ) : (
            <section className="panel" aria-label={t('notifications.feed')} style={{ overflow: 'hidden' }}>
              {groups.map((group) => (
                <div key={group.label}>
                  <div className="list-sub">{group.label}</div>
                  {group.items.map((notification) => (
                    <NotificationRow
                      key={notification.id}
                      notification={notification}
                      locale={locale}
                      onRead={() => void markRead(notification.id)}
                    />
                  ))}
                </div>
              ))}
              <div className="panel-f muted">
                <span>{t('notifications.footer')}</span>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  )
}

function NotificationRow({
  notification,
  locale,
  onRead,
}: {
  notification: Notification
  locale: Locale
  onRead: () => void
}) {
  const { t } = useApp()
  const unread = !notification.read
  const base = notification.entityType ? listPath(notification.entityType, '') : ''
  const target = base && notification.entityId ? `${base}/${notification.entityId}` : null
  const tone = KIND_TONE[notification.kind]
  return (
    <div className={unread ? 'list-item is-unread' : 'list-item'} style={{ alignItems: 'flex-start' }}>
      {unread ? (
        <span className="unread-dot" style={{ marginTop: 10 }} role="img" aria-label={t('notifications.unreadMark')} />
      ) : (
        <span className="wa-ntf-gap" />
      )}
      <span className={tone ? `feed-ico ${tone}` : 'feed-ico'}>
        <Icon name={KIND_ICON[notification.kind] ?? 'bell'} />
      </span>
      <div className="wa-ntf-text">
        <span style={unread ? { fontWeight: 500 } : undefined}>{notification.title}</span>
        {notification.body && <span className="t-sm secondary">{notification.body}</span>}
        <span className="t-xs muted">{t(`notifications.kind.${notification.kind}`)}</span>
      </div>
      <div className="wa-ntf-side">
        <span className="t-sm muted nowrap">{shortMoment(notification.createdAt, locale)}</span>
        {target && (
          <Link className="btn btn-ghost btn-sm" to={target} onClick={unread ? onRead : undefined}>
            {t('notifications.open')}
          </Link>
        )}
        {unread && (
          <button className="btn btn-ghost btn-sm" type="button" onClick={onRead}>
            {t('notifications.markRead')}
          </button>
        )}
      </div>
    </div>
  )
}

/** Сегодня · Вчера · Ранее — как в макете; «сегодня» решает часовой пояс смотрящего. */
function groupByDay(
  items: Notification[],
  now: Date,
  t: (key: string, p?: Record<string, string | number>) => string,
  locale: Locale,
): { label: string; items: Notification[] }[] {
  const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  const today = startOfDay(now)
  const buckets = { today: [] as Notification[], yesterday: [] as Notification[], earlier: [] as Notification[] }
  for (const item of items) {
    const at = startOfDay(new Date(item.createdAt))
    if (at >= today) buckets.today.push(item)
    else if (at >= today - 86_400_000) buckets.yesterday.push(item)
    else buckets.earlier.push(item)
  }
  const yesterdayDate = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' }).format(new Date(today - 86_400_000))
  return [
    { label: t('notifications.day.today', { count: buckets.today.length }), items: buckets.today },
    { label: t('notifications.day.yesterday', { date: yesterdayDate, count: buckets.yesterday.length }), items: buckets.yesterday },
    { label: t('notifications.day.earlier', { count: buckets.earlier.length }), items: buckets.earlier },
  ].filter((group) => group.items.length > 0)
}

function shortMoment(value: string, locale: Locale): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const sameDay = date.toDateString() === new Date().toDateString()
  return new Intl.DateTimeFormat(locale, sameDay ? { hour: '2-digit', minute: '2-digit' } : { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).format(date)
}
